import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, CreditCard, Zap, Rocket, Wallet, ArrowUpDown, Plus, Search, Menu, X, PieChart, Activity, Lock, CheckCircle, ArrowLeft } from 'lucide-react';

export default function MemeSwap() {
  const [balance, setBalance] = useState(0);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [coins, setCoins] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tradeAmount, setTradeAmount] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pulse');
  const [portfolio, setPortfolio] = useState([]);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: ''
  });
  const [showCoinModal, setShowCoinModal] = useState(false);
  const [selectedCoinForChart, setSelectedCoinForChart] = useState(null);
  const [chartTimeframe, setChartTimeframe] = useState('24H');
  const [notification, setNotification] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [chartHoverIndex, setChartHoverIndex] = useState(null);
  const [chartHoverPosition, setChartHoverPosition] = useState({ x: 0, y: 0 });

  // Generate mock trending meme coins with full chart data
  const generateCoins = () => {
    const memeNames = [
      'PEPE', 'DOGE', 'SHIB', 'FLOKI', 'BONK', 
      'WIF', 'BOME', 'MYRO', 'MEW', 'POPCAT',
      'MOODENG', 'BRETT', 'PONKE', 'GORILLA', 'SNEK'
    ];
    
    return memeNames.map((name, idx) => {
      const basePrice = Math.random() * 0.5;
      const baseMarketCap = Math.random() * 500000000;
      
      // Generate different timeframe data
      const generate24HData = () => {
        const points = 24;
        const priceData = [];
        const mcapData = [];
        
        for (let i = 0; i < points; i++) {
          const variance = (Math.random() - 0.5) * 0.2;
          const price = basePrice * (1 + variance);
          const mcap = baseMarketCap * (1 + variance);
          priceData.push(price);
          mcapData.push(mcap);
        }
        
        return { prices: priceData, mcaps: mcapData };
      };

      const generate7DData = () => {
        const points = 168; // 7 days * 24 hours
        const priceData = [];
        const mcapData = [];
        
        for (let i = 0; i < points; i++) {
          const trend = (i / points) * (Math.random() - 0.5) * 0.5;
          const variance = (Math.random() - 0.5) * 0.15;
          const price = basePrice * (1 + trend + variance);
          const mcap = baseMarketCap * (1 + trend + variance);
          priceData.push(price);
          mcapData.push(mcap);
        }
        
        return { prices: priceData, mcaps: mcapData };
      };

      const generate30DData = () => {
        const points = 720; // 30 days * 24 hours
        const priceData = [];
        const mcapData = [];
        
        for (let i = 0; i < points; i++) {
          const trend = (i / points) * (Math.random() - 0.5) * 0.8;
          const variance = (Math.random() - 0.5) * 0.2;
          const price = basePrice * (1 + trend + variance);
          const mcap = baseMarketCap * (1 + trend + variance);
          priceData.push(price);
          mcapData.push(mcap);
        }
        
        return { prices: priceData, mcaps: mcapData };
      };

      const data24H = generate24HData();
      const data7D = generate7DData();
      const data30D = generate30DData();

      return {
        id: idx,
        name,
        symbol: name,
        price: basePrice.toFixed(6),
        change24h: (Math.random() * 40 - 20).toFixed(2),
        volume24h: (Math.random() * 10000000).toFixed(0),
        marketCap: baseMarketCap.toFixed(0),
        holders: Math.floor(Math.random() * 50000) + 1000,
        chart: data24H.prices.slice(-24), // Mini chart uses last 24 points
        chartData: {
          '24H': { prices: data24H.prices, mcaps: data24H.mcaps },
          '7D': { prices: data7D.prices, mcaps: data7D.mcaps },
          '30D': { prices: data30D.prices, mcaps: data30D.mcaps }
        }
      };
    });
  };

  useEffect(() => {
    setCoins(generateCoins());
  }, []);

  // Auto-refresh coins every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    setCoins(prevCoins => 
      prevCoins.map(coin => {
        const priceChange = 1 + (Math.random() * 0.1 - 0.05);
        const newPrice = parseFloat(coin.price) * priceChange;
        const newMcap = parseFloat(coin.marketCap) * priceChange;
        
        // Update all chart timeframes with new data point
        const updateChartData = (oldData) => {
          return {
            prices: [...oldData.prices.slice(1), newPrice],
            mcaps: [...oldData.mcaps.slice(1), newMcap]
          };
        };

        return {
          ...coin,
          price: newPrice.toFixed(6),
          marketCap: newMcap.toFixed(0),
          change24h: (Math.random() * 40 - 20).toFixed(2),
          chart: [...coin.chart.slice(1), newPrice],
          chartData: {
            '24H': updateChartData(coin.chartData['24H']),
            '7D': updateChartData(coin.chartData['7D']),
            '30D': updateChartData(coin.chartData['30D'])
          }
        };
      })
    );
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleAddFunds = (amount) => {
    setBalance(prev => prev + amount);
    setShowPayment(false);
  };

  const handleTrade = (type, coin = selectedCoin, sellAllAmount = null) => {
    const amount = sellAllAmount !== null ? sellAllAmount : parseFloat(tradeAmount);
    if (!amount || !coin) return;

    const cost = amount * parseFloat(coin.price);
    
    if (type === 'buy' && balance >= cost) {
      setBalance(prev => prev - cost);
      
      // Add to portfolio
      setPortfolio(prev => {
        const existing = prev.find(p => p.symbol === coin.symbol);
        if (existing) {
          const totalAmount = existing.amount + amount;
          const totalCost = existing.totalCost + cost;
          return prev.map(p => 
            p.symbol === coin.symbol 
              ? { ...p, amount: totalAmount, totalCost, avgPrice: totalCost / totalAmount }
              : p
          );
        } else {
          return [...prev, {
            symbol: coin.symbol,
            name: coin.name,
            amount,
            avgPrice: parseFloat(coin.price),
            totalCost: cost,
            currentPrice: parseFloat(coin.price)
          }];
        }
      });
      
      setNotification({ type: 'success', message: `Bought ${amount.toFixed(4)} ${coin.symbol} for $${cost.toFixed(2)}` });
      setTimeout(() => setNotification(null), 3000);
      setShowCoinModal(false);
    } else if (type === 'sell') {
      const holding = portfolio.find(p => p.symbol === coin.symbol);
      if (!holding || holding.amount < amount) {
        setNotification({ type: 'error', message: 'Insufficient holdings' });
        setTimeout(() => setNotification(null), 3000);
        return;
      }
      
      // Calculate PNL for this trade
      const avgPrice = holding.avgPrice;
      const currentPrice = parseFloat(coin.price);
      const pnl = (currentPrice - avgPrice) * amount;
      const pnlPercent = ((currentPrice - avgPrice) / avgPrice) * 100;
      
      setBalance(prev => prev + cost);
      
      // Record trade history
      const trade = {
        id: Date.now(),
        symbol: coin.symbol,
        name: coin.name,
        type: 'sell',
        amount: amount,
        buyPrice: avgPrice,
        sellPrice: currentPrice,
        pnl: pnl,
        pnlPercent: pnlPercent,
        timestamp: new Date().toISOString(),
        total: cost
      };
      
      setTradeHistory(prev => [trade, ...prev]);
      
      // Update portfolio
      setPortfolio(prev => {
        const updated = prev.map(p => {
          if (p.symbol === coin.symbol) {
            const newAmount = p.amount - amount;
            if (newAmount <= 0.0001) return null; // Remove if almost nothing left
            const ratio = newAmount / p.amount;
            return { ...p, amount: newAmount, totalCost: p.totalCost * ratio };
          }
          return p;
        }).filter(Boolean);
        return updated;
      });
      
      const pnlDisplay = pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
      setNotification({ 
        type: 'success', 
        message: `Sold ${amount.toFixed(4)} ${coin.symbol} for $${cost.toFixed(2)} (${pnlDisplay})` 
      });
      setTimeout(() => setNotification(null), 3000);
      setShowCoinModal(false);
    } else {
      setNotification({ type: 'error', message: 'Insufficient balance' });
      setTimeout(() => setNotification(null), 3000);
    }
    setTradeAmount('');
  };

  const handleSellAll = (coin) => {
    const holding = portfolio.find(p => p.symbol === coin.symbol);
    if (holding) {
      handleTrade('sell', coin, holding.amount);
    }
  };

  const handleCheckout = () => {
    if (!selectedAmount) return;
    setShowPayment(false);
    setShowCheckout(true);
    setCheckoutStep(1);
  };

  const processPayment = () => {
    // Simulate payment processing
    setCheckoutStep(3);
    setTimeout(() => {
      setCheckoutStep(4); // Success state
      setTimeout(() => {
        setBalance(prev => prev + selectedAmount);
        setShowCheckout(false);
        setSelectedAmount(null);
        setCardDetails({ number: '', name: '', expiry: '', cvv: '' });
        setCheckoutStep(1);
      }, 2000);
    }, 2000);
  };

  // Update current prices in portfolio continuously
  useEffect(() => {
    if (portfolio.length > 0 && coins.length > 0) {
      setPortfolio(prev => prev.map(holding => {
        const coin = coins.find(c => c.symbol === holding.symbol);
        return coin ? { ...holding, currentPrice: parseFloat(coin.price) } : holding;
      }));
    }
  }, [coins]);

  // Calculate total portfolio value and PNL
  const portfolioValue = portfolio.reduce((sum, holding) => 
    sum + (holding.amount * holding.currentPrice), 0
  );
  
  const totalInvested = portfolio.reduce((sum, holding) => sum + holding.totalCost, 0);
  const totalPnL = portfolioValue - totalInvested;
  const pnlPercentage = totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0;

  const handleCoinClick = (coin) => {
    setSelectedCoinForChart(coin);
    setShowCoinModal(true);
    setChartTimeframe('24H');
  };

  const filteredCoins = coins.filter(coin => 
    coin.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const MiniChart = ({ data }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    
    return (
      <svg width="80" height="30" className="mini-chart">
        <polyline
          points={data.map((val, i) => `${i * (80/23)},${30 - ((val - min) / range * 28)}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  const FullChart = ({ data, timeframe, coin }) => {
    if (!data || !data.prices || data.prices.length === 0) return null;
    
    const prices = data.prices;
    const mcaps = data.mcaps;
    const max = Math.max(...prices);
    const min = Math.min(...prices);
    const range = max - min || 1;
    
    const width = 800;
    const height = 400;
    const padding = { top: 20, right: 20, bottom: 40, left: 70 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Create points for the line
    const points = prices.map((val, i) => {
      const x = padding.left + (i / (prices.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((val - min) / range * chartHeight);
      return `${x},${y}`;
    }).join(' ');

    // Create points for the gradient fill
    const fillPoints = `${padding.left},${padding.top + chartHeight} ` + 
                       points + 
                       ` ${padding.left + chartWidth},${padding.top + chartHeight}`;

    // Generate Y-axis labels
    const yLabels = 5;
    const yLabelValues = Array(yLabels).fill(0).map((_, i) => {
      return min + (range * (yLabels - 1 - i) / (yLabels - 1));
    });

    // Format date/time based on timeframe
    const formatTime = (index, total) => {
      if (timeframe === '24H') {
        const hour = Math.floor((index / total) * 24);
        return `${hour}:00`;
      } else if (timeframe === '7D') {
        const day = Math.floor((index / total) * 7);
        return `${day}d ago`;
      } else {
        const day = Math.floor((index / total) * 30);
        return `${day}d ago`;
      }
    };

    // Handle mouse move on chart
    const handleMouseMove = (e) => {
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Convert mouse position to data index
      const relativeX = mouseX - padding.left;
      if (relativeX < 0 || relativeX > chartWidth) {
        setChartHoverIndex(null);
        return;
      }
      
      const dataIndex = Math.round((relativeX / chartWidth) * (prices.length - 1));
      setChartHoverIndex(dataIndex);
      setChartHoverPosition({ x: mouseX, y: mouseY });
    };

    const handleMouseLeave = () => {
      setChartHoverIndex(null);
    };

    return (
      <div style={{ position: 'relative' }}>
        <svg 
          width="100%" 
          height="400" 
          viewBox={`0 0 ${width} ${height}`} 
          className="full-chart"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: 'crosshair' }}
        >
          <defs>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffc107" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ffc107" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {yLabelValues.map((_, i) => {
            const y = padding.top + (i * chartHeight / (yLabels - 1));
            return (
              <line
                key={i}
                x1={padding.left}
                y1={y}
                x2={padding.left + chartWidth}
                y2={y}
                stroke="#e0e0e0"
                strokeWidth="2"
              />
            );
          })}

          {/* Y-axis labels */}
          {yLabelValues.map((value, i) => {
            const y = padding.top + (i * chartHeight / (yLabels - 1));
            return (
              <text
                key={i}
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                fill="#78909c"
                fontSize="12"
                fontFamily="JetBrains Mono, monospace"
                fontWeight="600"
              >
                ${value.toFixed(6)}
              </text>
            );
          })}

          {/* Gradient fill under line */}
          <polygon
            points={fillPoints}
            fill="url(#chartGradient)"
          />

          {/* Main line */}
          <polyline
            points={points}
            fill="none"
            stroke="#ff9800"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Hover crosshair and point */}
          {chartHoverIndex !== null && (
            <>
              {/* Vertical line */}
              <line
                x1={padding.left + (chartHoverIndex / (prices.length - 1)) * chartWidth}
                y1={padding.top}
                x2={padding.left + (chartHoverIndex / (prices.length - 1)) * chartWidth}
                y2={padding.top + chartHeight}
                stroke="#6b7280"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              
              {/* Horizontal line */}
              <line
                x1={padding.left}
                y1={padding.top + chartHeight - ((prices[chartHoverIndex] - min) / range * chartHeight)}
                x2={padding.left + chartWidth}
                y2={padding.top + chartHeight - ((prices[chartHoverIndex] - min) / range * chartHeight)}
                stroke="#6b7280"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              
              {/* Hover point */}
              <circle
                cx={padding.left + (chartHoverIndex / (prices.length - 1)) * chartWidth}
                cy={padding.top + chartHeight - ((prices[chartHoverIndex] - min) / range * chartHeight)}
                r="5"
                fill="#10b981"
                stroke="#0a0b0d"
                strokeWidth="2"
              />
            </>
          )}
        </svg>

        {/* Tooltip */}
        {chartHoverIndex !== null && (
          <div
            style={{
              position: 'absolute',
              top: chartHoverPosition.y - 100,
              left: chartHoverPosition.x + 10,
              background: '#0a0b0d',
              border: '1px solid #1a1c1f',
              borderRadius: '8px',
              padding: '0.75rem',
              pointerEvents: 'none',
              zIndex: 1000,
              minWidth: '200px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontFamily: 'Inter, sans-serif' }}>
              {formatTime(chartHoverIndex, prices.length)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>Price:</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#10b981', fontFamily: 'JetBrains Mono, monospace' }}>
                  ${prices[chartHoverIndex].toFixed(6)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>Market Cap:</span>
                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: 'white', fontFamily: 'JetBrains Mono, monospace' }}>
                  ${(mcaps[chartHoverIndex] / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="memeswap-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          overflow-x: hidden;
        }

        .memeswap-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #4fc3f7 0%, #29b6f6 50%, #03a9f4 100%);
          font-family: 'Fredoka', sans-serif;
          color: #2c3e50;
          position: relative;
        }

        .memeswap-container::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 193, 7, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .header {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 3px solid #ffa726;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 0;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          gap: 2rem;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 700;
          font-size: 1.5rem;
          color: #ff9800;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
          transform: rotate(-15deg);
        }

        .nav {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .nav-link {
          color: #546e7a;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .nav-link:hover {
          color: #ff9800;
          background: rgba(255, 152, 0, 0.1);
          transform: translateY(-2px);
        }

        .nav-link.active {
          color: white;
          background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .balance-display {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1rem;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .add-funds-btn {
          background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
          border: none;
          color: white;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(102, 187, 106, 0.3);
        }

        .add-funds-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 187, 106, 0.4);
        }

        .withdraw-btn {
          background: white;
          border: 3px solid #e0e0e0;
          color: #546e7a;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .withdraw-btn:hover {
          border-color: #ff9800;
          color: #ff9800;
          transform: translateY(-2px);
        }

        .menu-btn {
          display: none;
          background: white;
          border: 3px solid #e0e0e0;
          color: #546e7a;
          padding: 0.5rem;
          border-radius: 10px;
          cursor: pointer;
        }

        .main-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
          position: relative;
          z-index: 1;
        }

        .search-bar {
          margin-bottom: 1.5rem;
          position: relative;
        }

        .search-input {
          width: 100%;
          background: white;
          border: 3px solid #e0e0e0;
          padding: 1rem 1rem 1rem 3rem;
          border-radius: 16px;
          color: #2c3e50;
          font-family: 'Fredoka', sans-serif;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .search-input:focus {
          outline: none;
          border-color: #ffc107;
          box-shadow: 0 4px 20px rgba(255, 193, 7, 0.2);
          transform: translateY(-2px);
        }

        .search-input::placeholder {
          color: #90a4ae;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #90a4ae;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .refresh-btn {
          background: white;
          border: 3px solid #e0e0e0;
          color: #546e7a;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          font-weight: 600;
          font-family: 'Fredoka', sans-serif;
          font-size: 0.95rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .refresh-btn:hover {
          border-color: #ffc107;
          color: #ff9800;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
        }

        .coins-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
        }

        .coin-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .coin-card::before {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          background: linear-gradient(135deg, #ffc107, #ff9800, #f57c00);
          border-radius: 20px;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
        }

        .coin-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 152, 0, 0.2);
        }

        .coin-card:hover::before {
          opacity: 1;
        }

        .coin-card.selected {
          border-color: #ffc107;
          background: #fff8e1;
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.3);
        }

        .coin-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 1rem;
        }

        .coin-info h3 {
          font-size: 1.375rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          color: #2c3e50;
        }

        .coin-symbol {
          color: #78909c;
          font-size: 0.95rem;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 600;
        }

        .coin-price {
          text-align: right;
        }

        .price-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
        }

        .price-change {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 700;
          margin-top: 0.375rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .price-change.positive {
          background: #c8e6c9;
          color: #2e7d32;
        }

        .price-change.negative {
          background: #ffcdd2;
          color: #c62828;
        }

        .coin-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1.25rem;
          padding-top: 1.25rem;
          border-top: 2px solid #f5f5f5;
        }

        .stat {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .stat-label {
          color: #78909c;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-weight: 700;
          font-size: 1rem;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
        }

        .mini-chart {
          color: #ffc107;
          margin-top: 1rem;
        }

        .trade-panel {
          position: sticky;
          top: 100px;
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.75rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .trade-title {
          font-size: 1.375rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #2c3e50;
        }

        .trade-input-group {
          margin-bottom: 1.25rem;
        }

        .trade-label {
          display: block;
          color: #546e7a;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .trade-input {
          width: 100%;
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          padding: 1rem;
          border-radius: 12px;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
          font-size: 1.125rem;
          font-weight: 700;
          transition: all 0.3s ease;
        }

        .trade-input:focus {
          outline: none;
          border-color: #ffc107;
          background: white;
        }

        .trade-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.875rem;
        }

        .trade-btn {
          padding: 1rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          border: none;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .buy-btn {
          background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
          color: white;
        }

        .buy-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 187, 106, 0.3);
        }

        .sell-btn {
          background: linear-gradient(135deg, #ef5350 0%, #e53935 100%);
          color: white;
        }

        .sell-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(239, 83, 80, 0.3);
        }

        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 24px;
          padding: 2rem;
          max-width: 480px;
          width: 100%;
          animation: slideUp 0.3s ease;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .modal-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #2c3e50;
        }

        .close-btn {
          background: #f5f5f5;
          border: none;
          color: #546e7a;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: #e0e0e0;
          transform: rotate(90deg);
        }

        .payment-options {
          display: grid;
          gap: 0.875rem;
        }

        .payment-option {
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          padding: 1.375rem;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .payment-option:hover {
          border-color: #ffc107;
          background: #fff8e1;
          transform: translateX(4px);
        }

        .payment-option.selected {
          border-color: #ffc107;
          background: #fff8e1;
        }

        .payment-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ff9800;
          font-family: 'JetBrains Mono', monospace;
        }

        .checkout-content {
          max-width: 520px;
        }

        .checkout-steps {
          display: flex;
          margin-bottom: 2rem;
          position: relative;
        }

        .checkout-steps::before {
          content: '';
          position: absolute;
          top: 18px;
          left: 20px;
          right: 20px;
          height: 3px;
          background: #e0e0e0;
        }

        .step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          z-index: 1;
        }

        .step-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: white;
          border: 3px solid #e0e0e0;
          color: #90a4ae;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .step.active .step-circle {
          background: linear-gradient(135deg, #ffc107, #ff9800);
          border-color: #ffc107;
          color: white;
        }

        .step.completed .step-circle {
          background: linear-gradient(135deg, #66bb6a, #43a047);
          border-color: #66bb6a;
          color: white;
        }

        .step-label {
          font-size: 0.8rem;
          color: #78909c;
          font-weight: 600;
        }

        .step.active .step-label {
          color: #ff9800;
          font-weight: 700;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-label {
          display: block;
          color: #546e7a;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-input {
          width: 100%;
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          padding: 1rem;
          border-radius: 12px;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
          font-size: 1rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #ffc107;
          background: white;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .checkout-btn {
          width: 100%;
          background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
          border: none;
          color: white;
          padding: 1.125rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
          box-shadow: 0 4px 12px rgba(102, 187, 106, 0.3);
        }

        .checkout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 187, 106, 0.4);
        }

        .checkout-btn.secondary {
          background: white;
          border: 3px solid #e0e0e0;
          color: #546e7a;
        }

        .checkout-btn.secondary:hover {
          border-color: #ffc107;
          color: #ff9800;
        }

        .security-note {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 1rem;
          background: #e3f2fd;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          color: #1976d2;
          font-size: 0.9rem;
          font-weight: 600;
          border: 2px solid #bbdefb;
        }

        .processing {
          text-align: center;
          padding: 3rem 2rem;
        }

        .spinner {
          width: 56px;
          height: 56px;
          border: 5px solid #e0e0e0;
          border-top-color: #ffc107;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .success-content {
          text-align: center;
          padding: 2rem 0;
        }

        .success-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #66bb6a, #43a047);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
          box-shadow: 0 8px 24px rgba(102, 187, 106, 0.3);
        }

        .portfolio-grid {
          display: grid;
          gap: 1rem;
        }

        .portfolio-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .stat-card-label {
          color: #78909c;
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-card-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-card-change {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 1rem;
          font-weight: 700;
          margin-top: 0.75rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-card-change.positive {
          color: #43a047;
        }

        .stat-card-change.negative {
          color: #e53935;
        }

        .holding-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .holding-card:hover {
          border-color: #ffc107;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.15);
        }

        .holding-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.25rem;
        }

        .holding-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2c3e50;
        }

        .holding-symbol {
          color: #78909c;
          font-size: 0.95rem;
          font-family: 'JetBrains Mono', monospace;
          margin-top: 0.25rem;
          font-weight: 600;
        }

        .holding-pnl {
          text-align: right;
        }

        .pnl-value {
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }

        .holding-details {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
          padding-top: 1.25rem;
          border-top: 2px solid #f5f5f5;
        }

        .holding-detail {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .detail-label {
          color: #78909c;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-weight: 700;
          font-size: 1rem;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: white;
        }

        .empty-state-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .coin-modal-content {
          max-width: 900px;
          width: 100%;
        }

        .coin-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid #f5f5f5;
        }

        .coin-modal-info {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .coin-modal-name {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .coin-modal-name h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #2c3e50;
        }

        .coin-modal-name .symbol {
          color: #78909c;
          font-size: 1.125rem;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 600;
        }

        .coin-modal-price {
          text-align: right;
        }

        .coin-modal-price .price {
          font-size: 2.25rem;
          font-weight: 700;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
          margin-bottom: 0.375rem;
        }

        .timeframe-selector {
          display: flex;
          gap: 0.625rem;
          background: #f5f5f5;
          padding: 0.5rem;
          border-radius: 14px;
          margin-bottom: 1.5rem;
        }

        .timeframe-btn {
          padding: 0.625rem 1.125rem;
          background: transparent;
          border: none;
          color: #546e7a;
          font-family: 'Fredoka', sans-serif;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .timeframe-btn:hover {
          color: #ff9800;
          background: white;
        }

        .timeframe-btn.active {
          background: linear-gradient(135deg, #ffc107, #ff9800);
          color: white;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .chart-container {
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.75rem;
          margin-bottom: 1.5rem;
        }

        .full-chart {
          width: 100%;
          height: auto;
        }

        .coin-modal-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .coin-modal-trade {
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.75rem;
        }

        .coin-modal-trade h3 {
          font-size: 1.375rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
        }

        .holdings-info {
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
          border: 3px solid #a5d6a7;
          border-radius: 16px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .holdings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .holdings-label {
          color: #2e7d32;
          font-size: 0.9rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .holdings-value {
          color: #1b5e20;
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }

        .holdings-pnl {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding-top: 1rem;
          border-top: 2px solid rgba(46, 125, 50, 0.2);
        }

        .holdings-pnl-label {
          color: #2e7d32;
          font-size: 0.9rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .holdings-pnl-value {
          font-size: 1.125rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
          flex: 1;
          text-align: right;
        }

        .sell-all-btn {
          width: 100%;
          background: linear-gradient(135deg, #ef5350, #e53935);
          border: none;
          color: white;
          padding: 1rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 12px rgba(239, 83, 80, 0.3);
        }

        .sell-all-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(239, 83, 80, 0.4);
        }

        .trade-history-section {
          margin-top: 2rem;
        }

        .trade-history-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1.25rem;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .trade-history-list {
          display: grid;
          gap: 1rem;
        }

        .trade-item {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 1.5rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .trade-item:hover {
          border-color: #ffc107;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.15);
        }

        .trade-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .trade-item-coin {
          font-weight: 700;
          color: #2c3e50;
          font-size: 1.125rem;
        }

        .trade-item-time {
          color: #78909c;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .trade-item-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .trade-detail {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .trade-detail-label {
          color: #78909c;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .trade-detail-value {
          font-weight: 700;
          font-size: 1rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .paypal-option {
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          padding: 1.5rem;
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .paypal-option:hover {
          border-color: #0070ba;
          background: #e3f2fd;
          transform: translateY(-2px);
        }

        .paypal-logo {
          width: 90px;
          height: 56px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.375rem;
          color: #0070ba;
          font-family: 'Verdana', sans-serif;
          font-style: italic;
          padding: 0.625rem;
          border: 3px solid #e0e0e0;
        }

        .paypal-info {
          flex: 1;
        }

        .paypal-title {
          font-weight: 700;
          font-size: 1.125rem;
          color: #2c3e50;
          margin-bottom: 0.375rem;
        }

        .paypal-desc {
          color: #78909c;
          font-size: 0.95rem;
          font-weight: 600;
        }

        .withdraw-amount-input {
          width: 100%;
          background: #f5f5f5;
          border: 3px solid #e0e0e0;
          padding: 1.25rem;
          border-radius: 16px;
          color: #2c3e50;
          font-family: 'JetBrains Mono', monospace;
          font-size: 1.75rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
        }

        .withdraw-amount-input:focus {
          outline: none;
          border-color: #ffc107;
          background: white;
        }

        .withdraw-info {
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
          border: 3px solid #a5d6a7;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          color: #1b5e20;
          font-size: 0.95rem;
          font-weight: 700;
          text-align: center;
        }

        .about-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .about-hero {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
          border-radius: 24px;
          padding: 4rem 3rem;
          margin-bottom: 3rem;
          text-align: center;
          border: 3px solid #e0e0e0;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-block;
          background: linear-gradient(135deg, #ffc107, #ff9800);
          color: white;
          padding: 0.625rem 1.5rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .about-hero-title {
          font-size: 3.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .gradient-text {
          background: linear-gradient(135deg, #ffc107, #ff9800);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .about-hero-subtitle {
          font-size: 1.5rem;
          color: #546e7a;
          font-weight: 600;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .about-section {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 24px;
          padding: 3rem;
          margin-bottom: 2rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
        }

        .about-section.highlight {
          background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
          border-color: #ffc107;
        }

        .section-badge {
          display: inline-block;
          background: #f5f5f5;
          border: 2px solid #e0e0e0;
          color: #546e7a;
          padding: 0.5rem 1.25rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 0.95rem;
          margin-bottom: 1.25rem;
        }

        .about-section-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
        }

        .about-text {
          font-size: 1.25rem;
          color: #546e7a;
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }

        .about-text-large {
          font-size: 1.375rem;
          color: #2c3e50;
          line-height: 1.8;
          margin-bottom: 1.5rem;
          font-weight: 500;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .feature-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          border-color: #ffc107;
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.2);
        }

        .feature-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .feature-card h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 0.75rem;
        }

        .feature-card p {
          font-size: 1rem;
          color: #546e7a;
          line-height: 1.6;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .stat-box {
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
          border: 3px solid #a5d6a7;
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
        }

        .stat-number {
          font-size: 3rem;
          font-weight: 700;
          color: #1b5e20;
          margin-bottom: 0.5rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-label {
          font-size: 1rem;
          color: #2e7d32;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .steps-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          margin-top: 2rem;
        }

        .step-card {
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 20px;
          padding: 2.5rem;
          text-align: center;
          position: relative;
          transition: all 0.3s ease;
        }

        .step-card:hover {
          border-color: #ffc107;
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.2);
        }

        .step-number {
          position: absolute;
          top: -20px;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #ffc107, #ff9800);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .step-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1rem;
          margin-top: 1rem;
        }

        .step-card p {
          font-size: 1.125rem;
          color: #546e7a;
          line-height: 1.7;
        }

        .about-cta {
          background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
          border-radius: 24px;
          padding: 4rem 3rem;
          text-align: center;
          margin-top: 3rem;
          box-shadow: 0 12px 40px rgba(255, 152, 0, 0.3);
        }

        .about-cta h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
        }

        .about-cta p {
          font-size: 1.375rem;
          color: rgba(255, 255, 255, 0.95);
          font-weight: 600;
          margin-bottom: 2rem;
        }

        .cta-button {
          background: white;
          color: #ff9800;
          border: none;
          padding: 1.25rem 3rem;
          border-radius: 16px;
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'Fredoka', sans-serif;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .cta-button:hover {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
        }

        .notification {
          position: fixed;
          top: 100px;
          right: 2rem;
          background: white;
          border: 3px solid #e0e0e0;
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.875rem;
          z-index: 10000;
          animation: slideInRight 0.3s ease;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .main-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
          position: relative;
        }

        .search-bar {
          margin-bottom: 1.5rem;
          position: relative;
        }

        .search-input {
          width: 100%;
          background: #151619;
          border: 1px solid #1a1c1f;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border-radius: 10px;
          color: white;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #10b981;
          background: #0a0b0d;
        }

        .search-input::placeholder {
          color: #6b7280;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
        }

        .refresh-btn {
          background: #151619;
          border: 1px solid #1a1c1f;
          color: #9ca3af;
          padding: 0.625rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          font-family: 'Inter', sans-serif;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .refresh-btn:hover {
          background: #1a1c1f;
          color: white;
        }

        .coins-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .coin-card {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .coin-card:hover {
          border-color: #2a2c31;
          background: #1a1c1f;
        }

        .coin-card.selected {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.05);
        }

        .coin-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 1rem;
        }

        .coin-info h3 {
          font-size: 1.125rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          color: white;
        }

        .coin-symbol {
          color: #6b7280;
          font-size: 0.875rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .coin-price {
          text-align: right;
        }

        .price-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: white;
          font-family: 'JetBrains Mono', monospace;
        }

        .price-change {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-top: 0.25rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .price-change.positive {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .price-change.negative {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .coin-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.875rem;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #1a1c1f;
        }

        .stat {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .stat-value {
          font-weight: 600;
          font-size: 0.9rem;
          color: #e4e5e7;
          font-family: 'JetBrains Mono', monospace;
        }

        .mini-chart {
          color: #10b981;
          margin-top: 0.75rem;
          opacity: 0.7;
        }

        .trade-panel {
          position: sticky;
          top: 100px;
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .trade-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: white;
        }

        .trade-input-group {
          margin-bottom: 1.25rem;
        }

        .trade-label {
          display: block;
          color: #9ca3af;
          font-size: 0.825rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .trade-input {
          width: 100%;
          background: #0a0b0d;
          border: 1px solid #1a1c1f;
          padding: 0.875rem;
          border-radius: 8px;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 1rem;
          font-weight: 600;
        }

        .trade-input:focus {
          outline: none;
          border-color: #10b981;
        }

        .trade-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }

        .trade-btn {
          padding: 0.875rem;
          border-radius: 8px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .buy-btn {
          background: #10b981;
          color: white;
        }

        .buy-btn:hover {
          background: #059669;
        }

        .sell-btn {
          background: #ef4444;
          color: white;
        }

        .sell-btn:hover {
          background: #dc2626;
        }

        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: #0a0b0d;
          border: 1px solid #1a1c1f;
          border-radius: 16px;
          padding: 2rem;
          max-width: 480px;
          width: 100%;
          animation: slideUp 0.25s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.05);
          border: none;
          color: #9ca3af;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .payment-options {
          display: grid;
          gap: 0.75rem;
        }

        .payment-option {
          background: #151619;
          border: 1px solid #1a1c1f;
          padding: 1.25rem;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .payment-option:hover {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.05);
        }

        .payment-option.selected {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.1);
        }

        .payment-amount {
          font-size: 1.375rem;
          font-weight: 700;
          color: white;
          font-family: 'JetBrains Mono', monospace;
        }

        .checkout-content {
          max-width: 520px;
        }

        .checkout-steps {
          display: flex;
          margin-bottom: 2rem;
          position: relative;
        }

        .checkout-steps::before {
          content: '';
          position: absolute;
          top: 16px;
          left: 16px;
          right: 16px;
          height: 2px;
          background: #1a1c1f;
        }

        .step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          z-index: 1;
        }

        .step-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #1a1c1f;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .step.active .step-circle {
          background: #10b981;
          color: white;
        }

        .step.completed .step-circle {
          background: #10b981;
          color: white;
        }

        .step-label {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .step.active .step-label {
          color: white;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-label {
          display: block;
          color: #9ca3af;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .form-input {
          width: 100%;
          background: #151619;
          border: 1px solid #1a1c1f;
          padding: 0.875rem;
          border-radius: 8px;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.95rem;
        }

        .form-input:focus {
          outline: none;
          border-color: #10b981;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .checkout-btn {
          width: 100%;
          background: #10b981;
          border: none;
          color: white;
          padding: 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 1rem;
        }

        .checkout-btn:hover {
          background: #059669;
        }

        .checkout-btn.secondary {
          background: #151619;
          color: #9ca3af;
        }

        .checkout-btn.secondary:hover {
          background: #1a1c1f;
          color: white;
        }

        .security-note {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 8px;
          margin-bottom: 1.5rem;
          color: #10b981;
          font-size: 0.825rem;
        }

        .processing {
          text-align: center;
          padding: 3rem 2rem;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #1a1c1f;
          border-top-color: #10b981;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .success-content {
          text-align: center;
          padding: 2rem 0;
        }

        .success-icon {
          width: 64px;
          height: 64px;
          background: rgba(16, 185, 129, 0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: #10b981;
        }

        .portfolio-grid {
          display: grid;
          gap: 1rem;
        }

        .portfolio-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.25rem;
        }

        .stat-card-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-card-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-card-change {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          font-weight: 600;
          margin-top: 0.5rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-card-change.positive {
          color: #10b981;
        }

        .stat-card-change.negative {
          color: #ef4444;
        }

        .holding-card {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.25rem;
        }

        .holding-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .holding-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: white;
        }

        .holding-symbol {
          color: #6b7280;
          font-size: 0.875rem;
          font-family: 'JetBrains Mono', monospace;
          margin-top: 0.125rem;
        }

        .holding-pnl {
          text-align: right;
        }

        .pnl-value {
          font-size: 1.125rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }

        .holding-details {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #1a1c1f;
        }

        .holding-detail {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .detail-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .detail-value {
          font-weight: 600;
          font-size: 0.9rem;
          color: #e4e5e7;
          font-family: 'JetBrains Mono', monospace;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #6b7280;
        }

        .empty-state-icon {
          width: 64px;
          height: 64px;
          background: #151619;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .coin-modal-content {
          max-width: 900px;
          width: 100%;
        }

        .coin-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #1a1c1f;
        }

        .coin-modal-info {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .coin-modal-name {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .coin-modal-name h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
        }

        .coin-modal-name .symbol {
          color: #6b7280;
          font-size: 1rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .coin-modal-price {
          text-align: right;
        }

        .coin-modal-price .price {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          margin-bottom: 0.25rem;
        }

        .timeframe-selector {
          display: flex;
          gap: 0.5rem;
          background: #151619;
          padding: 0.375rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
        }

        .timeframe-btn {
          padding: 0.5rem 1rem;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-family: 'Inter', sans-serif;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          border-radius: 7px;
          transition: all 0.2s ease;
        }

        .timeframe-btn:hover {
          color: white;
          background: rgba(255, 255, 255, 0.05);
        }

        .timeframe-btn.active {
          background: #10b981;
          color: white;
        }

        .chart-container {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .full-chart {
          width: 100%;
          height: auto;
        }

        .coin-modal-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .coin-modal-trade {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .coin-modal-trade h3 {
          font-size: 1.125rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1.25rem;
        }

        .holdings-info {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 10px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .holdings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .holdings-label {
          color: #9ca3af;
          font-size: 0.825rem;
          font-weight: 500;
        }

        .holdings-value {
          color: #10b981;
          font-size: 1.125rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
        }

        .holdings-pnl {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(16, 185, 129, 0.2);
        }

        .holdings-pnl-label {
          color: #9ca3af;
          font-size: 0.825rem;
          font-weight: 500;
        }

        .holdings-pnl-value {
          font-size: 1rem;
          font-weight: 700;
          font-family: 'JetBrains Mono', monospace;
          flex: 1;
          text-align: right;
        }

        .sell-all-btn {
          width: 100%;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
          padding: 0.75rem;
          border-radius: 8px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 1.5rem;
        }

        .sell-all-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: #ef4444;
        }

        .trade-history-section {
          margin-top: 2rem;
        }

        .trade-history-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
        }

        .trade-history-list {
          display: grid;
          gap: 0.75rem;
        }

        .trade-item {
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 10px;
          padding: 1rem;
        }

        .trade-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .trade-item-coin {
          font-weight: 700;
          color: white;
          font-size: 1rem;
        }

        .trade-item-time {
          color: #6b7280;
          font-size: 0.75rem;
        }

        .trade-item-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .trade-detail {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .trade-detail-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .trade-detail-value {
          font-weight: 600;
          font-size: 0.875rem;
          font-family: 'JetBrains Mono', monospace;
        }

        .paypal-option {
          background: #151619;
          border: 1px solid #1a1c1f;
          padding: 1.5rem;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .paypal-option:hover {
          border-color: #0070ba;
          background: rgba(0, 112, 186, 0.05);
        }

        .paypal-logo {
          width: 80px;
          height: 50px;
          background: white;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
          color: #0070ba;
          font-family: 'Verdana', sans-serif;
          font-style: italic;
          padding: 0.5rem;
        }

        .paypal-info {
          flex: 1;
        }

        .paypal-title {
          font-weight: 700;
          font-size: 1rem;
          color: white;
          margin-bottom: 0.25rem;
        }

        .paypal-desc {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .withdraw-amount-input {
          width: 100%;
          background: #151619;
          border: 1px solid #1a1c1f;
          padding: 1rem;
          border-radius: 10px;
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .withdraw-amount-input:focus {
          outline: none;
          border-color: #10b981;
        }

        .withdraw-info {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          padding: 0.875rem;
          margin-bottom: 1.5rem;
          color: #10b981;
          font-size: 0.875rem;
          text-align: center;
        }

        .notification {
          position: fixed;
          top: 100px;
          right: 2rem;
          background: #151619;
          border: 1px solid #1a1c1f;
          border-radius: 12px;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          z-index: 10000;
          animation: slideInRight 0.3s ease;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }

        .notification.success {
          border-color: #66bb6a;
          background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
        }

        .notification.error {
          border-color: #ef5350;
          background: linear-gradient(135deg, #ffebee, #ffcdd2);
        }

        .notification-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .notification.success .notification-icon {
          color: #2e7d32;
        }

        .notification.error .notification-icon {
          color: #c62828;
        }

        .notification-message {
          font-size: 1rem;
          font-weight: 700;
          color: #2c3e50;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .layout-grid {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 1.5rem;
          align-items: start;
        }

        @media (max-width: 1200px) {
          .layout-grid {
            grid-template-columns: 1fr;
          }

          .trade-panel {
            position: static;
          }

          .coin-modal-stats {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .header-content {
            flex-wrap: wrap;
          }

          .nav {
            display: none;
          }

          .menu-btn {
            display: block;
          }

          .coins-grid {
            grid-template-columns: 1fr;
          }

          .portfolio-stats {
            grid-template-columns: 1fr;
          }

          .holding-details {
            grid-template-columns: repeat(2, 1fr);
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .coin-modal-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .coin-modal-info {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
          }

          .coin-modal-price {
            text-align: left;
          }

          .coin-modal-stats {
            grid-template-columns: 1fr;
          }

          .timeframe-selector {
            flex-wrap: wrap;
          }

          .trade-item-details {
            grid-template-columns: 1fr;
          }

          .section-title {
            font-size: 1.5rem;
          }

          .trade-history-title {
            font-size: 1.375rem;
          }

          .about-hero {
            padding: 2.5rem 1.5rem;
          }

          .about-hero-title {
            font-size: 2rem;
          }

          .about-hero-subtitle {
            font-size: 1.125rem;
          }

          .about-section {
            padding: 2rem 1.5rem;
          }

          .about-section-title {
            font-size: 1.75rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }

          .steps-container {
            grid-template-columns: 1fr;
          }

          .about-cta {
            padding: 2.5rem 1.5rem;
          }

          .about-cta h2 {
            font-size: 1.75rem;
          }

          .about-cta p {
            font-size: 1.125rem;
          }

          .cta-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">
              <Zap size={18} color="white" strokeWidth={2.5} />
            </div>
            MemeSwap
          </div>
          
          <nav className="nav">
            <div 
              className={`nav-link ${activeTab === 'pulse' ? 'active' : ''}`}
              onClick={() => setActiveTab('pulse')}
            >
              Pulse
            </div>
            <div 
              className={`nav-link ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => setActiveTab('portfolio')}
            >
              Portfolio
            </div>
            <div 
              className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
            >
              About
            </div>
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="balance-display">
              <Wallet size={18} />
              ${balance.toFixed(2)}
            </div>
            
            <button className="add-funds-btn" onClick={() => setShowPayment(true)}>
              <Plus size={18} />
              Add Funds
            </button>

            <button className="withdraw-btn" onClick={() => setShowWithdraw(true)}>
              <ArrowUpDown size={18} />
              Withdraw
            </button>

            <button className="menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-content">
        {activeTab === 'pulse' && (
          <>
            <div className="search-bar">
              <Search className="search-icon" size={18} />
              <input 
                type="text"
                placeholder="Search by token name..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="section-header">
              <h1 className="section-title">Pulse</h1>
              <button 
                className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                onClick={handleRefresh}
              >
                <Activity size={16} />
                Refresh
              </button>
            </div>

            <div className="layout-grid">
              {/* Coins Grid */}
              <div className="coins-grid">
                {filteredCoins.map(coin => (
                  <div 
                    key={coin.id}
                    className={`coin-card ${selectedCoin?.id === coin.id ? 'selected' : ''}`}
                    onClick={() => handleCoinClick(coin)}
                  >
                    <div className="coin-header">
                      <div className="coin-info">
                        <h3>{coin.name}</h3>
                        <div className="coin-symbol">${coin.symbol}</div>
                      </div>
                      <div className="coin-price">
                        <div className="price-value">${coin.price}</div>
                        <div className={`price-change ${parseFloat(coin.change24h) >= 0 ? 'positive' : 'negative'}`}>
                          {parseFloat(coin.change24h) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {Math.abs(coin.change24h)}%
                        </div>
                      </div>
                    </div>

                    <MiniChart data={coin.chart} />

                    <div className="coin-stats">
                      <div className="stat">
                        <div className="stat-label">Volume 24h</div>
                        <div className="stat-value">${(coin.volume24h / 1000000).toFixed(2)}M</div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Market Cap</div>
                        <div className="stat-value">${(coin.marketCap / 1000000).toFixed(1)}M</div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Holders</div>
                        <div className="stat-value">{coin.holders.toLocaleString()}</div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Status</div>
                        <div className="stat-value" style={{ color: '#10b981' }}>Live</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trade Panel */}
              {selectedCoin && (
                <div className="trade-panel">
                  <h2 className="trade-title">Trade {selectedCoin.name}</h2>
                  
                  <div className="trade-input-group">
                    <label className="trade-label">Amount</label>
                    <input 
                      type="number"
                      className="trade-input"
                      placeholder="0.00"
                      value={tradeAmount}
                      onChange={(e) => setTradeAmount(e.target.value)}
                    />
                  </div>

                  <div className="trade-input-group">
                    <label className="trade-label">Total Cost</label>
                    <div className="trade-input" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                      ${(parseFloat(tradeAmount || 0) * parseFloat(selectedCoin.price)).toFixed(2)}
                    </div>
                  </div>

                  <div className="trade-buttons">
                    <button className="trade-btn buy-btn" onClick={() => handleTrade('buy')}>
                      <TrendingUp size={18} />
                      Buy
                    </button>
                    <button className="trade-btn sell-btn" onClick={() => handleTrade('sell')}>
                      <TrendingDown size={18} />
                      Sell
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'portfolio' && (
          <>
            <div className="section-header">
              <h1 className="section-title">Portfolio</h1>
            </div>

            {portfolio.length > 0 ? (
              <>
                <div className="portfolio-stats">
                  <div className="stat-card">
                    <div className="stat-card-label">Total Value</div>
                    <div className="stat-card-value">${portfolioValue.toFixed(2)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Total Invested</div>
                    <div className="stat-card-value">${totalInvested.toFixed(2)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Total P&L</div>
                    <div className="stat-card-value" style={{ color: totalPnL >= 0 ? '#10b981' : '#ef4444' }}>
                      ${totalPnL.toFixed(2)}
                    </div>
                    <div className={`stat-card-change ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                      {totalPnL >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                      {Math.abs(pnlPercentage).toFixed(2)}%
                    </div>
                  </div>
                </div>

                <div className="portfolio-grid">
                  {portfolio.map(holding => {
                    const currentValue = holding.amount * holding.currentPrice;
                    const pnl = currentValue - holding.totalCost;
                    const pnlPercent = (pnl / holding.totalCost) * 100;
                    
                    return (
                      <div key={holding.symbol} className="holding-card">
                        <div className="holding-header">
                          <div>
                            <div className="holding-name">{holding.name}</div>
                            <div className="holding-symbol">${holding.symbol}</div>
                          </div>
                          <div className="holding-pnl">
                            <div className="pnl-value" style={{ color: pnl >= 0 ? '#10b981' : '#ef4444' }}>
                              {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                            </div>
                            <div className={`price-change ${pnl >= 0 ? 'positive' : 'negative'}`} style={{ marginTop: '0.25rem' }}>
                              {pnl >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {Math.abs(pnlPercent).toFixed(2)}%
                            </div>
                          </div>
                        </div>

                        <div className="holding-details">
                          <div className="holding-detail">
                            <div className="detail-label">Amount</div>
                            <div className="detail-value">{holding.amount.toFixed(4)}</div>
                          </div>
                          <div className="holding-detail">
                            <div className="detail-label">Avg Price</div>
                            <div className="detail-value">${holding.avgPrice.toFixed(6)}</div>
                          </div>
                          <div className="holding-detail">
                            <div className="detail-label">Current Price</div>
                            <div className="detail-value">${holding.currentPrice.toFixed(6)}</div>
                          </div>
                          <div className="holding-detail">
                            <div className="detail-label">Value</div>
                            <div className="detail-value">${currentValue.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Trade History Section */}
                {tradeHistory.length > 0 && (
                  <div className="trade-history-section">
                    <h2 className="trade-history-title">Trade History</h2>
                    <div className="trade-history-list">
                      {tradeHistory.map(trade => (
                        <div key={trade.id} className="trade-item">
                          <div className="trade-item-header">
                            <div className="trade-item-coin">
                              {trade.type === 'sell' ? '📤' : '📥'} {trade.name} ({trade.symbol})
                            </div>
                            <div className="trade-item-time">
                              {new Date(trade.timestamp).toLocaleString()}
                            </div>
                          </div>
                          <div className="trade-item-details">
                            <div className="trade-detail">
                              <div className="trade-detail-label">Amount</div>
                              <div className="trade-detail-value">{trade.amount.toFixed(4)}</div>
                            </div>
                            <div className="trade-detail">
                              <div className="trade-detail-label">Buy Price</div>
                              <div className="trade-detail-value">${trade.buyPrice.toFixed(6)}</div>
                            </div>
                            <div className="trade-detail">
                              <div className="trade-detail-label">Sell Price</div>
                              <div className="trade-detail-value">${trade.sellPrice.toFixed(6)}</div>
                            </div>
                            <div className="trade-detail">
                              <div className="trade-detail-label">P&L</div>
                              <div 
                                className="trade-detail-value" 
                                style={{ color: trade.pnl >= 0 ? '#10b981' : '#ef4444', fontSize: '1rem', fontWeight: '700' }}
                              >
                                {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)} ({trade.pnl >= 0 ? '+' : ''}{trade.pnlPercent.toFixed(2)}%)
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <PieChart size={32} color="#6b7280" />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#9ca3af' }}>
                  No holdings yet
                </h3>
                <p style={{ fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                  Start trading to build your portfolio
                </p>
                <button className="add-funds-btn" onClick={() => setActiveTab('pulse')}>
                  <Rocket size={18} />
                  Explore Coins
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'about' && (
          <>
            <div className="about-container">
              <div className="about-hero">
                <div className="about-hero-content">
                  <div className="hero-badge">⚡ Revolutionary</div>
                  <h1 className="about-hero-title">
                    The First Meme Coin Exchange
                    <span className="gradient-text"> Built for Everyone</span>
                  </h1>
                  <p className="about-hero-subtitle">
                    No wallets. No crypto confusion. Just your credit card and the memes you love.
                  </p>
                </div>
              </div>

              <div className="about-section">
                <div className="section-badge">💡 Our Mission</div>
                <h2 className="about-section-title">Breaking Down the Barriers</h2>
                <p className="about-text">
                  We noticed something crazy: millions of people wanted to trade meme coins, but the barrier to entry was insane. Download a wallet? Buy crypto on another platform? Transfer it? Connect wallets? Gas fees? 
                </p>
                <p className="about-text">
                  <strong>We said no.</strong> That's not how retail works. That's not how normal people want to invest.
                </p>
              </div>

              <div className="about-section highlight">
                <div className="section-badge">🚀 The MemeSwap Difference</div>
                <h2 className="about-section-title">Trade Like You Shop Online</h2>
                <div className="features-grid">
                  <div className="feature-card">
                    <div className="feature-icon">💳</div>
                    <h3>Credit Card In, Cash Out</h3>
                    <p>Use your Visa, Mastercard, or PayPal. Withdraw directly to PayPal. No crypto middleman required.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">⚡</div>
                    <h3>Instant Trading</h3>
                    <p>See a coin pumping? Buy it now. Want to take profits? Sell instantly. No waiting for blockchain confirmations.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">📱</div>
                    <h3>Simple as Social Media</h3>
                    <p>If you can use Instagram, you can use MemeSwap. One click to buy, one click to sell. That's it.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🎯</div>
                    <h3>Real-Time Portfolio</h3>
                    <p>See your gains (and losses) update live. Track every trade. Know exactly what you're making.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🔒</div>
                    <h3>Bank-Level Security</h3>
                    <p>Your funds are protected with the same security your bank uses. No seed phrases to lose, no wallet hacks.</p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon">🌟</div>
                    <h3>No Hidden Fees</h3>
                    <p>What you see is what you get. No gas fees, no slippage, no surprise charges. Just straight-up trading.</p>
                  </div>
                </div>
              </div>

              <div className="about-section">
                <div className="section-badge">🎭 For the Retail Trader</div>
                <h2 className="about-section-title">We Built This for YOU</h2>
                <p className="about-text">
                  Every feature was designed with retail traders in mind. You shouldn't need a computer science degree to trade meme coins. You shouldn't have to watch 3-hour YouTube tutorials to understand wallets.
                </p>
                <div className="stats-grid">
                  <div className="stat-box">
                    <div className="stat-number">$0</div>
                    <div className="stat-label">Minimum Deposit</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">3</div>
                    <div className="stat-label">Steps to Start Trading</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">30s</div>
                    <div className="stat-label">Average Signup Time</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">24/7</div>
                    <div className="stat-label">Support Available</div>
                  </div>
                </div>
              </div>

              <div className="about-section">
                <div className="section-badge">💎 How It Works</div>
                <h2 className="about-section-title">Three Steps to Trading Meme Coins</h2>
                <div className="steps-container">
                  <div className="step-card">
                    <div className="step-number">1</div>
                    <h3>Add Funds</h3>
                    <p>Click "Add Funds", choose your amount ($50-$1000), enter your credit card. Done in 30 seconds.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-number">2</div>
                    <h3>Pick Your Coins</h3>
                    <p>Browse trending meme coins, check the charts, see what's pumping. Click any coin to view details and trade.</p>
                  </div>
                  <div className="step-card">
                    <div className="step-number">3</div>
                    <h3>Trade & Profit</h3>
                    <p>Buy when you think it'll go up. Sell when you want to take profits. Watch your portfolio grow in real-time.</p>
                  </div>
                </div>
              </div>

              <div className="about-section highlight">
                <div className="section-badge">🌈 Our Vision</div>
                <h2 className="about-section-title">Making Meme Coins Accessible to Everyone</h2>
                <p className="about-text-large">
                  We believe meme coins are fun, exciting, and shouldn't be locked behind technical barriers. 
                  Whether you're a college student with $50 or a professional with $5,000, you deserve easy access 
                  to the world's most entertaining financial market.
                </p>
                <p className="about-text-large">
                  <strong>MemeSwap isn't just a platform—it's a movement to democratize meme coin trading.</strong>
                </p>
              </div>

              <div className="about-cta">
                <h2>Ready to Start Trading?</h2>
                <p>Join thousands of traders who've ditched their wallets for something better.</p>
                <button className="cta-button" onClick={() => setActiveTab('pulse')}>
                  <Rocket size={24} />
                  Start Trading Now
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <div className="modal" onClick={() => setShowPayment(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Funds</h2>
              <button className="close-btn" onClick={() => setShowPayment(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="payment-options">
              {[50, 100, 250, 500, 1000].map(amount => (
                <div 
                  key={amount}
                  className={`payment-option ${selectedAmount === amount ? 'selected' : ''}`}
                  onClick={() => setSelectedAmount(amount)}
                >
                  <div>
                    <CreditCard size={20} style={{ marginBottom: '0.375rem', color: '#10b981' }} />
                    <div style={{ color: '#9ca3af', fontSize: '0.825rem' }}>Credit/Debit Card</div>
                  </div>
                  <div className="payment-amount">${amount}</div>
                </div>
              ))}
            </div>

            <button 
              className="checkout-btn" 
              onClick={handleCheckout}
              disabled={!selectedAmount}
              style={{ opacity: selectedAmount ? 1 : 0.5, cursor: selectedAmount ? 'pointer' : 'not-allowed' }}
            >
              Continue to Checkout
            </button>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="modal" onClick={() => {
          if (checkoutStep !== 3 && checkoutStep !== 4) {
            setShowCheckout(false);
            setSelectedAmount(null);
            setCheckoutStep(1);
          }
        }}>
          <div className="modal-content checkout-content" onClick={(e) => e.stopPropagation()}>
            {checkoutStep < 3 && (
              <div className="modal-header">
                <button 
                  className="close-btn" 
                  onClick={() => {
                    setShowCheckout(false);
                    setShowPayment(true);
                    setCheckoutStep(1);
                  }}
                  style={{ marginRight: 'auto' }}
                >
                  <ArrowLeft size={18} />
                </button>
                <h2 className="modal-title" style={{ flex: 1, textAlign: 'center', marginRight: '32px' }}>
                  Checkout
                </h2>
              </div>
            )}

            {checkoutStep === 1 && (
              <>
                <div className="checkout-steps">
                  <div className="step active">
                    <div className="step-circle">1</div>
                    <div className="step-label">Amount</div>
                  </div>
                  <div className="step">
                    <div className="step-circle">2</div>
                    <div className="step-label">Payment</div>
                  </div>
                  <div className="step">
                    <div className="step-circle">3</div>
                    <div className="step-label">Confirm</div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Amount to Add</label>
                  <div className="form-input" style={{ fontSize: '1.75rem', fontWeight: '700', color: '#10b981', textAlign: 'center', padding: '1.5rem' }}>
                    ${selectedAmount}
                  </div>
                </div>

                <div className="security-note">
                  <Lock size={16} />
                  <span>Secure payment powered by Stripe</span>
                </div>

                <button className="checkout-btn" onClick={() => setCheckoutStep(2)}>
                  Continue to Payment
                </button>
              </>
            )}

            {checkoutStep === 2 && (
              <>
                <div className="checkout-steps">
                  <div className="step completed">
                    <div className="step-circle"><CheckCircle size={16} /></div>
                    <div className="step-label">Amount</div>
                  </div>
                  <div className="step active">
                    <div className="step-circle">2</div>
                    <div className="step-label">Payment</div>
                  </div>
                  <div className="step">
                    <div className="step-circle">3</div>
                    <div className="step-label">Confirm</div>
                  </div>
                </div>

                <div className="security-note">
                  <Lock size={16} />
                  <span>Your payment information is encrypted and secure</span>
                </div>

                <div>
                  <div className="form-group">
                    <label className="form-label">Card Number</label>
                    <input 
                      type="text"
                      className="form-input"
                      placeholder="1234 5678 9012 3456"
                      value={cardDetails.number}
                      onChange={(e) => setCardDetails({...cardDetails, number: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cardholder Name</label>
                    <input 
                      type="text"
                      className="form-input"
                      placeholder="John Doe"
                      value={cardDetails.name}
                      onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Expiry Date</label>
                      <input 
                        type="text"
                        className="form-input"
                        placeholder="MM/YY"
                        value={cardDetails.expiry}
                        onChange={(e) => setCardDetails({...cardDetails, expiry: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">CVV</label>
                      <input 
                        type="text"
                        className="form-input"
                        placeholder="123"
                        value={cardDetails.cvv}
                        onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                      />
                    </div>
                  </div>

                  <button 
                    type="button" 
                    className="checkout-btn"
                    onClick={processPayment}
                  >
                    Pay ${selectedAmount}
                  </button>
                </div>
              </>
            )}

            {checkoutStep === 3 && (
              <div className="processing">
                <div className="spinner"></div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: 'white' }}>
                  Processing Payment
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '0.95rem' }}>
                  Please wait while we process your transaction...
                </p>
              </div>
            )}

            {checkoutStep === 4 && (
              <div className="success-content">
                <div className="success-icon">
                  <CheckCircle size={32} />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem', color: 'white' }}>
                  Payment Successful!
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                  ${selectedAmount} has been added to your account
                </p>
                <div style={{ 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  padding: '1rem',
                  borderRadius: '8px',
                  color: '#10b981',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '1.25rem',
                  fontWeight: '700'
                }}>
                  New Balance: ${(balance + selectedAmount).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coin Detail Modal */}
      {showCoinModal && selectedCoinForChart && (
        <div className="modal" onClick={() => setShowCoinModal(false)}>
          <div className="modal-content coin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="coin-modal-header">
              <div className="coin-modal-info">
                <div className="coin-modal-name">
                  <h2>{selectedCoinForChart.name}</h2>
                  <div className="symbol">${selectedCoinForChart.symbol}</div>
                </div>
                <div className="coin-modal-price">
                  <div className="price">${selectedCoinForChart.price}</div>
                  <div className={`price-change ${parseFloat(selectedCoinForChart.change24h) >= 0 ? 'positive' : 'negative'}`}>
                    {parseFloat(selectedCoinForChart.change24h) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {Math.abs(selectedCoinForChart.change24h)}% (24h)
                  </div>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowCoinModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="timeframe-selector">
              {['24H', '7D', '30D'].map(tf => (
                <button
                  key={tf}
                  className={`timeframe-btn ${chartTimeframe === tf ? 'active' : ''}`}
                  onClick={() => setChartTimeframe(tf)}
                >
                  {tf}
                </button>
              ))}
            </div>

            <div className="chart-container">
              <FullChart 
                data={selectedCoinForChart.chartData[chartTimeframe]} 
                timeframe={chartTimeframe}
                coin={selectedCoinForChart}
              />
            </div>

            <div className="coin-modal-stats">
              <div className="stat-card">
                <div className="stat-card-label">Volume 24h</div>
                <div className="stat-card-value" style={{ fontSize: '1.25rem' }}>
                  ${(selectedCoinForChart.volume24h / 1000000).toFixed(2)}M
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">Market Cap</div>
                <div className="stat-card-value" style={{ fontSize: '1.25rem' }}>
                  ${(selectedCoinForChart.marketCap / 1000000).toFixed(1)}M
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">Holders</div>
                <div className="stat-card-value" style={{ fontSize: '1.25rem' }}>
                  {selectedCoinForChart.holders.toLocaleString()}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-label">24h Change</div>
                <div className="stat-card-value" style={{ fontSize: '1.25rem', color: parseFloat(selectedCoinForChart.change24h) >= 0 ? '#10b981' : '#ef4444' }}>
                  {parseFloat(selectedCoinForChart.change24h) >= 0 ? '+' : ''}{selectedCoinForChart.change24h}%
                </div>
              </div>
            </div>

            <div className="coin-modal-trade">
              <h3>Trade {selectedCoinForChart.name}</h3>
              
              {/* Show holdings if user owns this coin */}
              {(() => {
                const holding = portfolio.find(p => p.symbol === selectedCoinForChart.symbol);
                if (holding) {
                  const currentValue = holding.amount * holding.currentPrice;
                  const pnl = currentValue - holding.totalCost;
                  const pnlPercent = (pnl / holding.totalCost) * 100;
                  
                  return (
                    <>
                      <div className="holdings-info">
                        <div className="holdings-header">
                          <div className="holdings-label">Your Holdings</div>
                          <div className="holdings-value">{holding.amount.toFixed(4)} {holding.symbol}</div>
                        </div>
                        <div className="holdings-pnl">
                          <div className="holdings-pnl-label">P&L:</div>
                          <div className="holdings-pnl-value" style={{ color: pnl >= 0 ? '#10b981' : '#ef4444' }}>
                            {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({pnl >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%)
                          </div>
                        </div>
                      </div>
                      
                      <button 
                        className="sell-all-btn"
                        onClick={() => handleSellAll(selectedCoinForChart)}
                      >
                        Sell All ({holding.amount.toFixed(4)} {holding.symbol})
                      </button>
                    </>
                  );
                }
                return null;
              })()}
              
              <div className="trade-input-group">
                <label className="trade-label">Amount</label>
                <input 
                  type="number"
                  className="trade-input"
                  placeholder="0.00"
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                />
              </div>

              <div className="trade-input-group">
                <label className="trade-label">Total Cost</label>
                <div className="trade-input" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  ${(parseFloat(tradeAmount || 0) * parseFloat(selectedCoinForChart.price)).toFixed(2)}
                </div>
              </div>

              <div className="trade-buttons">
                <button 
                  className="trade-btn buy-btn" 
                  onClick={() => handleTrade('buy', selectedCoinForChart)}
                >
                  <TrendingUp size={18} />
                  Buy
                </button>
                <button 
                  className="trade-btn sell-btn" 
                  onClick={() => handleTrade('sell', selectedCoinForChart)}
                >
                  <TrendingDown size={18} />
                  Sell
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === 'success' ? <CheckCircle size={20} /> : <X size={20} />}
          </div>
          <div className="notification-message">{notification.message}</div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="modal" onClick={() => setShowWithdraw(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Withdraw Funds</h2>
              <button className="close-btn" onClick={() => setShowWithdraw(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Available Balance</label>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '700', 
                color: '#10b981', 
                fontFamily: 'JetBrains Mono, monospace',
                marginBottom: '1.5rem'
              }}>
                ${balance.toFixed(2)}
              </div>

              <label className="form-label">Withdrawal Amount</label>
              <input 
                type="number"
                className="withdraw-amount-input"
                placeholder="$0.00"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                max={balance}
              />

              {parseFloat(withdrawAmount) > 0 && (
                <div className="withdraw-info">
                  You will receive ${parseFloat(withdrawAmount).toFixed(2)} via PayPal
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ marginBottom: '0.75rem', display: 'block' }}>
                Select Withdrawal Method
              </label>
              
              <div 
                className="paypal-option"
                onClick={() => {
                  if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
                    setNotification({ type: 'error', message: 'Please enter withdrawal amount' });
                    setTimeout(() => setNotification(null), 3000);
                    return;
                  }
                  if (parseFloat(withdrawAmount) > balance) {
                    setNotification({ type: 'error', message: 'Insufficient balance' });
                    setTimeout(() => setNotification(null), 3000);
                    return;
                  }
                  // Redirect to PayPal
                  window.open('https://www.paypal.com/signin', '_blank');
                  setNotification({ 
                    type: 'success', 
                    message: 'Redirecting to PayPal for withdrawal...' 
                  });
                  setTimeout(() => {
                    setNotification(null);
                    setShowWithdraw(false);
                    setWithdrawAmount('');
                  }, 2000);
                }}
              >
                <div className="paypal-logo">
                  PayPal
                </div>
                <div className="paypal-info">
                  <div className="paypal-title">PayPal</div>
                  <div className="paypal-desc">Fast & secure withdrawal</div>
                </div>
                <ArrowUpDown size={20} color="#6b7280" />
              </div>
            </div>

            <div className="security-note">
              <Lock size={16} />
              <span>Withdrawals are processed securely via PayPal</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
